import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Switch, Button, Popconfirm, Typography, Space, Empty } from 'antd';
import { DeleteOutlined, EditOutlined, MenuOutlined, LinkOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DragSource, DropTarget, DragElementWrapper, DragSourceOptions, DropTargetMonitor } from 'react-dnd';
import { ColumnProps } from 'antd/lib/table';
import { endpoints } from '../../../config';
import { HomeShortcutsStore } from '../stores/HomeShortcutsStore';
import type { HomeShortcut } from '../types/HomeShortcut';

type Props = {
    store: HomeShortcutsStore;
};

let draggingIndex = -1;

type BeginDragProps = {
    index: number;
};

type RowTargetProps = {
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
};

const rowSource = {
    beginDrag(props: BeginDragProps) {
        draggingIndex = props.index;
        return {
            index: props.index
        };
    }
};

const rowTarget = {
    drop(props: RowTargetProps, monitor: DropTargetMonitor) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;

        if (dragIndex === hoverIndex) {
            return;
        }

        props.moveRow(dragIndex, hoverIndex);
        monitor.getItem().index = hoverIndex;
    }
};

type RowProps = {
    isOver: boolean;
    connectDragSource: DragElementWrapper<DragSourceOptions>;
    connectDropTarget: DragElementWrapper<unknown>;
    style: React.CSSProperties;
    className: string;
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
};

class DraggableBodyRowImpl extends React.Component<RowProps> {
    render() {
        const { isOver, connectDragSource, connectDropTarget, moveRow, index, ...restProps } = this.props;
        let className = restProps.className || '';

        if (isOver) {
            if (index > draggingIndex) {
                className += ' drop-over-downward';
            } else if (index < draggingIndex) {
                className += ' drop-over-upward';
            }
        }

        return connectDragSource(connectDropTarget(<tr {...restProps} className={className} />));
    }
}

const DraggableBodyRow = DropTarget('shortcut-row', rowTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver()
}))(
    DragSource('shortcut-row', rowSource, connect => ({
        connectDragSource: connect.dragSource()
    }))(DraggableBodyRowImpl)
);

const tableComponents = {
    body: {
        row: DraggableBodyRow
    }
};

const { Paragraph, Text } = Typography;

const ShortcutsList: React.FC<Props> = ({ store }) => {
    const { isSaving, isReordering } = store;

    const getImageUrl = (imagePath?: string | null) => {
        if (!imagePath) {
            return undefined;
        }

        return `${endpoints.imageUrl}?path=${encodeURIComponent(imagePath)}`;
    };

    const openLink = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const columns: ColumnProps<HomeShortcut>[] = [
        {
            title: '',
            key: 'drag',
            width: 48,
            render: () => <MenuOutlined style={{ color: '#9ba0ab', cursor: 'grab' }} />
        },
        {
            title: 'Image',
            dataIndex: 'imagePath',
            key: 'imagePath',
            width: 90,
            render: (imagePath?: string | null) => {
                const imageUrl = getImageUrl(imagePath);

                if (!imageUrl) {
                    return <Text type="secondary">No image</Text>;
                }

                return (
                    <img
                        src={imageUrl}
                        alt="shortcut"
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                    />
                );
            }
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (title: string, record: HomeShortcut) => (
                <div>
                    <div>{title}</div>
                    {record.description ? (
                        <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                            {record.description}
                        </Paragraph>
                    ) : null}
                </div>
            )
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            render: (url: string) => (
                <Space>
                    <Typography.Link href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                    </Typography.Link>
                    <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => openLink(url)}>
                        Test link
                    </Button>
                </Space>
            )
        },
        {
            title: 'Visible',
            dataIndex: 'isVisible',
            key: 'isVisible',
            width: 110,
            render: (isVisible: boolean, record: HomeShortcut) => (
                <Switch
                    checked={isVisible}
                    disabled={isSaving || isReordering}
                    onChange={checked => store.toggleVisibility(record, checked)}
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 140,
            render: (_: unknown, record: HomeShortcut) => (
                <Space size="small">
                    <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        disabled={isSaving || isReordering}
                        onClick={() => store.openEditModal(record)}
                    />
                    <Popconfirm
                        title={`Delete "${record.title}"?`}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => store.deleteShortcut(record.id)}
                    >
                        <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={isSaving || isReordering}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    if (!store.isLoading && store.customCards.length === 0) {
        return <Empty description="No custom home shortcuts yet." />;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <Table
                className="alpha-portal-table"
                loading={store.isLoading}
                rowKey="id"
                columns={columns}
                dataSource={store.customCards}
                pagination={false}
                components={store.customCards.length > 1 ? tableComponents : undefined}
                onRow={
                    store.customCards.length > 1 && !isSaving && !isReordering
                        ? (_record, index) =>
                              ({
                                  index,
                                  moveRow: store.reorder
                              }) as unknown as React.HTMLAttributes<HTMLElement>
                        : undefined
                }
            />
        </DndProvider>
    );
};

export default observer(ShortcutsList);
